import { Autocomplete, Button, Checkbox, TextField, createFilterOptions } from "@mui/material";
import { CSSProperties, FC, useLayoutEffect, useRef, useState } from "react";
import RiskGridBody from "./RiskGridBody";
import RiskLinkOption from "./RiskLinkOption";
import RiskSpinner from "./RiskSpinner";
import { ASSESSMENT_TAB, LEFT_COLUMNS, LINE_SELECT_WIDTH, RiskColumn, SELECT_WIDTH, SOURCE_LINE_WIDTH, TAB_COLUMNS, leftWidth, tabWidth } from "./riskColumns";
import { BulkValues, CheckState, EMPTY_OPTION, EditingCell, EditorRow, Filters, LinkIndex, RiskGridHandlers, SelectionState,
	caLabel, columnLinkOptions, domainOptions, intersectOptions, joinMultiValue, linkOptionLabel, linkOptionSearchText, splitMultiValue } from "./riskEditorState";
import './RiskGrid.scss';

interface Props {
	rows: EditorRow[];
	tab: string;
	readOnly: boolean;
	filters: Filters;
	filterOptions: Record<string, string[]>;
	onFilterChange: (columnId: string, value: string[] | string) => void;
	selection: SelectionState;
	headerCheck: CheckState;
	onToggleAll: () => void;
	bulk: BulkValues;
	onBulkChange: (columnId: string, value: string) => void;
	onApplyBulk: () => void;
	onClearBulk: () => void;
	bulkRisks: string[];
	canApplyBulk: boolean;
	editing: EditingCell | null;
	handlers: RiskGridHandlers;
	linkIndex: LinkIndex;
	options: Record<string, string[]>;
	busy: string;
}

const RiskGrid: FC<Props> = ({ rows, tab, readOnly, filters, filterOptions, onFilterChange, selection, headerCheck,
		onToggleAll, bulk, onBulkChange, onApplyBulk, onClearBulk, bulkRisks, canApplyBulk, editing, handlers,
		linkIndex, options, busy }) => {
	const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
	const [headerHeight, setHeaderHeight] = useState(0);
	const headerRef = useRef<HTMLDivElement>(null);
	const columns = TAB_COLUMNS[tab];
	const subTab = tab !== ASSESSMENT_TAB;
	const editableLines = !readOnly;

	useLayoutEffect(() => {
		const header = headerRef.current;
		if (!header) {
			return;
		}
		const measure = () => setHeaderHeight(header.getBoundingClientRect().height);
		measure();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const observer = new ResizeObserver(measure);
		observer.observe(header);
		return () => observer.disconnect();
	}, []);

	return (
		<div className="risk-grid" ref={setScrollElement} style={{ '--largura-esquerda': `${leftWidth()}px` } as CSSProperties}>
			<div className="grade" style={{ width: leftWidth() + tabWidth(tab) }}>
				<div className="cabecalho" ref={headerRef}>
					<div className="linha-cabecalho">
						<div className="esquerda">
							<div className="celula selecao" style={{ width: SELECT_WIDTH }}>
								{editableLines && (busy === 'toggleAll'
									? <RiskSpinner size={20}/>
									: <Checkbox
										size="small"
										checked={headerCheck === 'checked'}
										indeterminate={headerCheck === 'indeterminate'}
										disabled={!rows.length || !!busy}
										onChange={onToggleAll}
									/>
								)}
							</div>
							<div className="celula titulo" style={{ width: SOURCE_LINE_WIDTH }} title="Linha da aba Avaliação da planilha de origem">
								Linha
							</div>
							{LEFT_COLUMNS.map(column => (
								<div key={column.id} className="celula titulo" style={{ width: column.width }}>{column.label}</div>
							))}
						</div>
						<div className="direita">
							{subTab && <div className="celula selecao" style={{ width: LINE_SELECT_WIDTH }}/>}
							{columns.map(column => (
								<div key={column.id} className="celula titulo" style={{ width: column.width }} title={column.label}>
									{column.label}
								</div>
							))}
						</div>
					</div>
					{!readOnly && (
						<div className="linha-cabecalho filtros">
							<div className="esquerda">
								<div className="celula selecao" style={{ width: SELECT_WIDTH }}/>
								<div className="celula" style={{ width: SOURCE_LINE_WIDTH }}/>
								{LEFT_COLUMNS.map(column => (
									<div key={column.id} className="celula" style={{ width: column.width }}>{renderFilter(column)}</div>
								))}
							</div>
							<div className="direita">
								{subTab && <div className="celula selecao" style={{ width: LINE_SELECT_WIDTH }}/>}
								{columns.map(column => (
									<div key={column.id} className="celula" style={{ width: column.width }}>{renderFilter(column)}</div>
								))}
							</div>
						</div>
					)}
				</div>
				<RiskGridBody
					scrollElement={scrollElement}
					offset={headerHeight}
					rows={rows}
					tab={tab}
					readOnly={readOnly}
					selection={selection}
					editing={editing}
					handlers={handlers}
					linkIndex={linkIndex}
					options={options}
				/>
				{editableLines && (
					<div className="rodape">
						<div className="esquerda acoes-massa" style={{ width: leftWidth() }}>
							<Button
								size="small"
								variant="contained"
								startIcon={busy === 'applyBulk' ? <RiskSpinner size={16}/> : undefined}
								disabled={!canApplyBulk || !!busy}
								onClick={onApplyBulk}
							>
								Aplicar valor nos riscos selecionados
							</Button>
							<Button
								size="small"
								variant="outlined"
								startIcon={busy === 'clearBulk' ? <RiskSpinner size={16}/> : undefined}
								disabled={!!busy}
								onClick={onClearBulk}
							>
								Limpar valores
							</Button>
						</div>
						<div className="direita">
							{subTab && <div className="celula selecao" style={{ width: LINE_SELECT_WIDTH }}/>}
							{columns.map(column => (
								<div key={column.id} className="celula" style={{ width: column.width }}>{renderBulk(column)}</div>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);

	function optionLabel(column: RiskColumn, option: string) {
		if (option === '') {
			return '(vazio)';
		}
		return column.link === 'CA' ? caLabel(linkIndex, option) : option;
	}

	function renderFilter(column: RiskColumn) {
		const value = filters[column.id];
		if (column.kind === 'text' || column.multiple) {
			return (
				<TextField
					size="small"
					fullWidth
					placeholder="Contém..."
					value={typeof value === 'string' ? value : ''}
					onChange={(event) => onFilterChange(column.id, event.target.value)}
				/>
			);
		}
		const selected = Array.isArray(value) ? value : [];
		return (
			<Autocomplete
				multiple
				size="small"
				fullWidth
				limitTags={1}
				disableCloseOnSelect
				options={filterOptions[column.id] || []}
				value={selected}
				getOptionLabel={(option) => optionLabel(column, option)}
				onChange={(_, next) => onFilterChange(column.id, next)}
				renderInput={(params) => <TextField {...params} placeholder={selected.length ? '' : 'Filtrar'}/>}
			/>
		);
	}

	function renderBulk(column: RiskColumn) {
		const value = bulk[column.id] || '';
		if (column.kind === 'text') {
			return (
				<TextField
					size="small"
					fullWidth
					placeholder="Valor"
					value={value}
					onChange={(event) => onBulkChange(column.id, event.target.value)}
				/>
			);
		}
		if (column.kind === 'suggest') {
			return (
				<Autocomplete
					freeSolo
					size="small"
					fullWidth
					options={domainOptions(options, column)}
					inputValue={value}
					onInputChange={(_, next) => onBulkChange(column.id, next)}
					renderInput={(params) => <TextField {...params} placeholder="Valor"/>}
				/>
			);
		}
		const link = column.link;
		const available = link
			? intersectOptions(bulkRisks.map(risk => columnLinkOptions(linkIndex, risk, column)))
			: domainOptions(options, column);
		const helper = !link
			? undefined
			: !bulkRisks.length
				? 'Selecione riscos para ver os valores permitidos'
				: available.length
					? 'Valores permitidos para os riscos selecionados'
					: 'Nenhum valor em comum para os riscos selecionados';
		if (column.multiple) {
			return (
				<Autocomplete
					multiple
					size="small"
					fullWidth
					limitTags={1}
					disableCloseOnSelect
					disabled={!!link && !bulkRisks.length}
					options={available}
					value={splitMultiValue(value)}
					onChange={(_, next) => onBulkChange(column.id, joinMultiValue(next))}
					renderInput={(params) => <TextField {...params} placeholder={value ? '' : 'Valor'} helperText={helper}/>}
				/>
			);
		}
		return (
			<Autocomplete
				size="small"
				fullWidth
				disabled={!!link && !bulkRisks.length}
				options={[EMPTY_OPTION, ...available]}
				value={value || null}
				getOptionLabel={(option) => option === EMPTY_OPTION ? '(vazio)' : linkOptionLabel(linkIndex, column, option)}
				filterOptions={createFilterOptions<string>({
					stringify: (option) => option === EMPTY_OPTION ? '(vazio)' : linkOptionSearchText(linkIndex, column, option)
				})}
				renderOption={(props, option) => (
					<li {...props} key={option}>
						{option === EMPTY_OPTION ? '(vazio)' : <RiskLinkOption index={linkIndex} column={column} option={option}/>}
					</li>
				)}
				onChange={(_, next) => onBulkChange(column.id, next || '')}
				renderInput={(params) => <TextField {...params} placeholder="Valor" helperText={helper}/>}
			/>
		);
	}
};

export default RiskGrid;
